import { FetchBaseQueryError, FetchBaseQueryMeta, createApi, fetchBaseQuery } from "@reduxjs/toolkit/dist/query/react";
import { RootState } from '../store';

export interface ETLLastUpdate {
    rank: number;
    label: string;
    lastUpdate: string;
    frequency: string;
}

export interface ReleaseNote {
    version: string;
    content: string;
}

export const appSettingsApi = createApi({
    reducerPath: 'AppSettingsApi',
    tagTypes: ['AppSettings', 'SearchKeywords', 'ReleaseNotes'],
    baseQuery: fetchBaseQuery({
        baseUrl: `${process.env.PUBLIC_URL}/api/AppSettings/`,
        prepareHeaders: (headers, { getState }) => {
            const accessToken = (getState() as RootState).userStateReducer.accessToken;

            if (accessToken !== '') {
                headers.set('authorization', `Bearer ${accessToken}`)
            }

            return headers
        },
    }),
    endpoints: (build) => ({
        getLastUpdates: build.query<ETLLastUpdate[], string>({
            query: (version: string) => `GetLastUpdates?version=${version}`,
            providesTags: ['AppSettings'],
            transformErrorResponse: (failure: FetchBaseQueryError, meta: FetchBaseQueryMeta) => {
                let returnedError: string = 'An error occured while requesting last updates';
                switch (failure.status) {
                    case "FETCH_ERROR":
                        break;
                    case "PARSING_ERROR":
                        break;
                    case "TIMEOUT_ERROR":
                        break;
                    case "CUSTOM_ERROR":
                        break;
                }
                returnedError = `${failure.status}  - ${returnedError}`;
                return returnedError;
            }
        }),
        getSearchKeywords: build.query<string[], void>({
            query: () => `GetSearchKeywordsAsync`,
            providesTags: ['SearchKeywords'],
            transformErrorResponse: (failure: FetchBaseQueryError, meta: FetchBaseQueryMeta) => {
                let returnedError: string = 'An error occured while requesting search keywords';
                switch (failure.status) {
                    case "FETCH_ERROR":
                        break;
                    case "PARSING_ERROR":
                        break;
                    case "TIMEOUT_ERROR":
                        break;
                    case "CUSTOM_ERROR":
                        break;
                }
                returnedError = `${failure.status}  - ${returnedError}`;
                return returnedError;
            }
        }),
        logAction: build.mutation<Date, { action: string, data: string }>({
            query: ({ action, data }) => ({
                url: `LogActionAsync?action=${action}&data=${data}`,
                method: 'POST',
            }),
            transformErrorResponse: (failure: FetchBaseQueryError, meta: FetchBaseQueryMeta) => {
                let returnedError: string = 'An error occured while logging action';
                switch (failure.status) {
                    case "FETCH_ERROR":
                        break;
                    case "PARSING_ERROR":
                        break;
                    case "TIMEOUT_ERROR":
                        break;
                    case "CUSTOM_ERROR":
                        break;
                }
                returnedError = `${failure.status}  - ${returnedError}`;
                return returnedError;
            }
        }),
        exportToExcel: build.query<Blob, { searchString: string, exportType: string }>({
            query: ({ searchString, exportType }) => ({
                url: `ExportToExcel?searchString=${searchString}&exportType=${exportType}`,
                method: 'GET',
                responseHandler: async (response) => await response.blob(),
            }),
        }),
        getReleaseNotes: build.query<ReleaseNote[], void>({
            query: () => `GetReleaseNotes`,
            providesTags: ['ReleaseNotes'],
        })
    }),
})

export const { 
    useGetLastUpdatesQuery, 
    useGetSearchKeywordsQuery, 
    useLogActionMutation, 
    useExportToExcelQuery,
    useGetReleaseNotesQuery
} = appSettingsApi;
